import { useEffect, useState, useCallback, useMemo } from "react";
import { Clock, Filter, RefreshCw, Download, FileText, Users, Calendar, Coffee, AlertCircle, Search, Camera, CameraOff, X, LogIn as LogInIcon, Monitor } from "lucide-react";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { displayName } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ROLE_TONE = {
  admin: "bg-blue-100 text-blue-800",
  officer: "bg-slate-100 text-slate-700",
  staff: "bg-amber-100 text-amber-800",
};

function fmtMinutes(m) {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${String(r).padStart(2, "0")}m`;
}

function isoToLocal(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

function thisWeekBounds() {
  // Monday → Sunday in local time, formatted as YYYY-MM-DD
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // 0 = Mon
  const monday = new Date(now);
  monday.setDate(now.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(monday), to: iso(sunday) };
}

export default function TimesheetPage() {
  const defaults = useMemo(() => thisWeekBounds(), []);
  const [filters, setFilters] = useState({ q: "", user_id: "", from: defaults.from, to: defaults.to });
  const [shifts, setShifts] = useState([]);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [summary, setSummary] = useState({ rows: [] });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoView, setPhotoView] = useState(null); // {src, name, when, kind}

  const params = useCallback(() => ({
    user_id: filters.user_id || undefined,
    from: filters.from ? new Date(filters.from + "T00:00:00Z").toISOString() : undefined,
    to: filters.to ? new Date(filters.to + "T23:59:59Z").toISOString() : undefined,
  }), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sum, st] = await Promise.all([
        api.get("/timesheet/shifts", { params: { ...params(), limit: SERVER_PAGE } }),
        api.get("/timesheet/summary", { params: params() }),
        api.get("/officers"),
      ]);
      setShifts(s.data); setTotal(totalFrom(s));
      setSummary(sum.data);
      setStaff(st.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load timesheet");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const filteredShifts = useMemo(() => {
    if (!filters.q) return shifts;
    const q = filters.q.toLowerCase();
    return shifts.filter((s) => (s.user_name || "").toLowerCase().includes(q));
  }, [shifts, filters.q]);

  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem("vlog_token");
      const sp = new URLSearchParams();
      const p = params();
      Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== "") sp.append(k, v); });
      const url = `${API_BASE}/timesheet/export.${fmt}?${sp.toString()}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `logerd-timesheet-${stamp}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const totalMinutes = summary.rows.reduce((a, r) => a + (r.total_minutes || 0), 0);
  const peopleCount = summary.rows.length;
  const openShifts = shifts.filter((s) => s.status === "in" || s.status === "on_break").length;

  return (
    <div className="p-6 md:p-10" data-testid="timesheet-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Personnel</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Staff timesheet</h1>
          <p className="text-sm text-slate-600 mt-1">
            Clock-in / clock-out records from the kiosk. Filter by date or person, then export for payroll.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading} data-testid="timesheet-refresh" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => downloadExport("csv")} data-testid="timesheet-export-csv" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => downloadExport("pdf")} data-testid="timesheet-export-pdf" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:shadow transition">
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="timesheet-stats">
        <Tile label="Open shifts" value={openShifts} Icon={Clock} tone="amber" />
        <Tile label="People worked" value={peopleCount} Icon={Users} tone="primary" />
        <Tile label="Total worked" value={fmtMinutes(totalMinutes)} Icon={Calendar} tone="emerald" />
        <Tile label="Range" value={`${filters.from || "—"} → ${filters.to || "—"}`} Icon={Calendar} small />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="timesheet-filters">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={filters.q} onChange={set("q")} placeholder="Search name…" data-testid="timesheet-q" className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </div>
          <select value={filters.user_id} onChange={set("user_id")} data-testid="timesheet-person" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
            <option value="">Everyone</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
          </select>
          <input type="date" value={filters.from} onChange={set("from")} data-testid="timesheet-from" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          <input type="date" value={filters.to} onChange={set("to")} data-testid="timesheet-to" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
        </div>
      </section>

      {/* Weekly totals */}
      {summary.rows.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6" data-testid="timesheet-summary">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display text-lg font-semibold text-slate-900">Weekly totals</div>
            <div className="text-xs text-slate-500">Worked time per person, grouped by ISO week. Breaks excluded.</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Shifts</th>
                <th className="px-5 py-3">Weekly breakdown</th>
                <th className="px-5 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-100" data-testid={`timesheet-summary-${r.user_id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{displayName(r.name)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${ROLE_TONE[r.role] || "bg-slate-100 text-slate-700"}`}>{r.role}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{r.shift_count}</td>
                  <td className="px-5 py-3 text-slate-700 text-xs">
                    {Object.entries(r.weekly).map(([wk, mins]) => (
                      <span key={wk} className="inline-flex items-center gap-1 mr-2 mb-1 rounded bg-slate-50 border border-slate-200 px-2 py-0.5">
                        <span className="font-mono">{wk}</span> · <b>{fmtMinutes(mins)}</b>
                      </span>
                    ))}
                  </td>
                  <td className="px-5 py-3 text-right font-display text-lg font-bold text-slate-900">{fmtMinutes(r.total_minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Shifts table */}
      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="timesheet-shifts">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display text-lg font-semibold text-slate-900">Shifts</div>
          <div className="text-xs text-slate-500">{filteredShifts.length} record(s) in the selected range</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Clocked in</th>
              <th className="px-5 py-3">Clocked out</th>
              <th className="px-5 py-3">Break</th>
              <th className="px-5 py-3">Worked</th>
              <th className="px-5 py-3">Photos</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredShifts.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-5 py-14 text-center text-slate-500">No shifts in this range.</td></tr>
            )}
            {filteredShifts.map((s) => {
              const live = s.worked_minutes == null && (s.status === "in" || s.status === "on_break");
              const liveMin = live ? Math.max(0, Math.floor((Date.now() - new Date(s.clocked_in_at).getTime()) / 60000) - (s.break_minutes || 0)) : null;
              return (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`timesheet-row-${s.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>{displayName(s.user_name)}</span>
                      {s.source === "login" && (
                        <span
                          title="Tracked from admin browser login → logout"
                          className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700"
                          data-testid="timesheet-source-login"
                        >
                          <Monitor className="h-3 w-3" /> Login
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${ROLE_TONE[s.user_role] || "bg-slate-100 text-slate-700"}`}>{s.user_role}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{isoToLocal(s.clocked_in_at)}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{isoToLocal(s.clocked_out_at)}</td>
                  <td className="px-5 py-3 text-slate-700">
                    {s.break_minutes ? (
                      <span className="inline-flex items-center gap-1"><Coffee className="h-3.5 w-3.5 text-amber-500" /> {fmtMinutes(s.break_minutes)}</span>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{fmtMinutes(s.worked_minutes ?? liveMin)}</td>
                  <td className="px-5 py-3" data-testid={`timesheet-row-${s.id}-photos`}>
                    <PhotoCell
                      inPhoto={s.clock_in_photo}
                      outPhoto={s.clock_out_photo}
                      name={s.user_name}
                      inAt={s.clocked_in_at}
                      outAt={s.clocked_out_at}
                      onOpen={setPhotoView}
                    />
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                      s.status === "in" ? "bg-emerald-100 text-emerald-800"
                      : s.status === "on_break" ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"
                    }`}>{s.status === "in" ? "Working" : s.status === "on_break" ? "On break" : "Out"}</span>
                    {s.auto_closed && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700">
                        <AlertCircle className="h-3 w-3" /> auto-closed
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <LoadMoreBar shown={shifts.length} total={total} loading={moreLoading} testid="shifts-load-more" onMore={async () => {
          setMoreLoading(true);
          try { const res = await api.get("/timesheet/shifts", { params: { ...params(), limit: SERVER_PAGE, offset: shifts.length } }); setShifts((p) => [...p, ...res.data]); setTotal(totalFrom(res)); }
          finally { setMoreLoading(false); }
        }} />
      </section>

      <Dialog open={!!photoView} onOpenChange={(o) => !o && setPhotoView(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="timesheet-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" /> {photoView?.kind === "in" ? "Clock-in" : "Clock-out"} photo · {photoView?.name}
            </DialogTitle>
            <DialogDescription>
              Captured at the kiosk on {photoView?.when ? new Date(photoView.when).toLocaleString() : "—"}.
              Use this to verify the person actually present matches the staff member who clocked in.
            </DialogDescription>
          </DialogHeader>
          {photoView?.src ? (
            <img
              src={photoView.src}
              alt=""
              className="w-full rounded-lg border border-slate-200"
              style={{ transform: "scaleX(-1)" }}
              data-testid="timesheet-photo-full"
            />
          ) : (
            <div className="py-10 text-center text-slate-500 text-sm">No photo captured for this event.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoCell({ inPhoto, outPhoto, name, inAt, outAt, onOpen }) {
  const Thumb = ({ src, kind, when, label }) =>
    src ? (
      <button
        type="button"
        onClick={() => onOpen({ src, kind, name, when })}
        title={`${label} photo — click to enlarge`}
        data-testid={`timesheet-photo-thumb-${kind}`}
        className="h-10 w-10 rounded-md overflow-hidden border-2 border-slate-200 hover:border-primary transition flex-shrink-0"
      >
        <img src={src} alt={label} className="h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} />
      </button>
    ) : (
      <span
        title={`No ${label.toLowerCase()} photo`}
        className="h-10 w-10 rounded-md border-2 border-dashed border-slate-200 text-slate-300 flex items-center justify-center flex-shrink-0"
      >
        <CameraOff className="h-4 w-4" />
      </span>
    );
  return (
    <div className="flex items-center gap-1.5">
      <Thumb src={inPhoto} kind="in" when={inAt} label="Clock-in" />
      <Thumb src={outPhoto} kind="out" when={outAt} label="Clock-out" />
    </div>
  );
}

function Tile({ Icon, label, value, tone, small }) {
  const cls = {
    primary: "ring-primary/20 bg-primary/5",
    amber: "ring-amber-200 bg-amber-50",
    emerald: "ring-emerald-200 bg-emerald-50",
  }[tone] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 ${cls} ring-1`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <div className={`mt-2 font-display ${small ? "text-base" : "text-3xl"} font-bold tracking-tight text-slate-900`}>{value}</div>
    </div>
  );
}
